/**
 * Ranch / Semen and embryos. By sire or by tank: what's on hand, what open plans hold, and every
 * movement. Receiving, collecting, flushing, moving, selling and counting are each one sheet.
 */
import { useState } from "react";
import { Link, Route, Routes, useParams, useSearchParams } from "react-router";
import { localDate, type Money } from "../../../contract";
import { openEntity } from "../../../web/forms/EventForm";
import { RefPicker } from "../../../web/forms/pickers";
import { ApiError, api } from "../../../web/lib/api";
import { formatDate, formatMoney } from "../../../web/lib/format";
import { plural, t, useLanguage } from "../../../web/lib/i18n";
import { invalidateAll, useApi } from "../../../web/lib/query";
import { useCan, useMe } from "../../../web/lib/session";
import { registry } from "../../../web/registry";
import { Page } from "../../../web/shell/Layout";
import { Button, Card, Chip, Disclosure, Empty, ErrorNote, Field, IconButton, List, Loading, Menu, PageHeader, Row, Section, Segmented, Select, Sheet, Tabs, TextInput, cx, toast, useLocalState } from "../../../web/ui/kit";
import { EMBRYO_GRADES, EMBRYO_STAGES, INVENTORY_ACTIONS } from "../contract/events";
import { FREEZE_LABELS, MOVE_REASON_LABELS, RIGHTS_LABELS, SEXED_LABELS } from "../shared/labels";
import type { GeneticItemDTO, InventoryDTO, ItemDetailDTO, LotDTO } from "../shared/types";

const where = (l: Pick<LotDTO, "tank" | "canister" | "cane">) => [l.tank, l.canister && t("canister {{c}}", { c: l.canister }), l.cane && t("cane {{c}}", { c: l.cane })].filter(Boolean).join(", ");
const num = (v: string) => (v.trim() === "" || Number.isNaN(Number(v)) ? undefined : Number(v));
const money = (v: string, currency: string): Money | undefined => { const n = num(v.replace(/[$,]/g, "")); return n === undefined ? undefined : { minor: Math.round(n * 100), currency }; };

function useToday() {
  const { data: me } = useMe();
  return { today: me?.ranch ? localDate(new Date(), me.ranch.timezone) : new Date().toISOString().slice(0, 10), currency: me?.ranch?.currency ?? "USD" };
}

async function send(path: string, body: unknown, done: string, after: () => void) {
  try {
    await api.post(path, body);
    await invalidateAll();
    toast(done, { tone: "good" });
    after();
    return true;
  } catch (err) {
    toast(err instanceof ApiError ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" });
    return false;
  }
}

function Where({ v, set }: { v: { tank?: string; canister?: string; cane?: string }; set: (v: { tank?: string; canister?: string; cane?: string }) => void }) {
  return (
    <div className="genetics-target-row" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
      <TextInput aria-label={t("Semen tank")} placeholder={t("Semen tank")} value={v.tank ?? ""} onChange={(e) => set({ ...v, tank: e.target.value })} />
      <TextInput aria-label={t("Canister")} placeholder={t("Canister")} value={v.canister ?? ""} onChange={(e) => set({ ...v, canister: e.target.value })} />
      <TextInput aria-label={t("Cane")} placeholder={t("Cane")} value={v.cane ?? ""} onChange={(e) => set({ ...v, cane: e.target.value })} />
    </div>
  );
}

function ReceiveSheet({ open, onClose, sireId }: { open: boolean; onClose: () => void; sireId?: string }) {
  const { currency } = useToday();
  const [sire, setSire] = useState<string | null>(sireId ?? null);
  const [qty, setQty] = useState("");
  const [code, setCode] = useState("");
  const [loc, setLoc] = useState<{ tank?: string; canister?: string; cane?: string }>({});
  const [sexed, setSexed] = useState("conventional");
  const [origin, setOrigin] = useState<"purchased" | "opening">("purchased");
  const [from, setFrom] = useState<string | null>(null);
  const [cost, setCost] = useState("");
  const [owner, setOwner] = useState<string | null>(null);
  const [rights, setRights] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    await send("/m/genetics/inventory/receive", { sire_id: sire, quantity: num(qty), code: code || undefined, ...loc, sexed, origin, purchased_from: from ?? undefined, cost: money(cost, currency), owner_id: owner ?? undefined, rights: rights || undefined },
      t("Semen added"), onClose);
    setBusy(false);
  };
  return (
    <Sheet open={open} onClose={onClose} title={t("Receive semen")}>
      <form onSubmit={(e) => { e.preventDefault(); void submit(); }}>
        <Field label={t("Sire")} hint={t("An outside bull can be added as a reference animal.")}>
          <RefPicker target="animal" value={sire} onChange={(v) => setSire(v as string | null)} filter={{ sex: "male", status: "all" }} />
          {!sire && <Button size="sm" variant="ghost" icon="plus" onClick={() => openEntity({ kind: "animal", initial: { sex: "male", status: "reference" }, onDone: (id) => setSire(id) })}>{t("Add an outside bull")}</Button>}
        </Field>
        <Field label={t("Straws")}><TextInput inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value)} /></Field>
        <Field label={t("Where it's stored")}><Where v={loc} set={setLoc} /></Field>
        <Field label={t("Collection or lot code")} optional><TextInput className="mono" value={code} onChange={(e) => setCode(e.target.value)} /></Field>
        <Field label={t("This is")}><Segmented label={t("This is")} value={origin} onChange={setOrigin} options={[{ value: "purchased", label: t("A purchase") }, { value: "opening", label: t("What's already in the tank") }]} /></Field>
        <Disclosure label={t("More details")}>
          <Field label={t("Sorted")}><Select value={sexed} onChange={(e) => setSexed(e.target.value)}>{Object.entries(SEXED_LABELS).map(([k, l]) => <option key={k} value={k}>{t(l)}</option>)}</Select></Field>
          <Field label={t("Bought from")} optional><RefPicker target="contact" value={from} onChange={(v) => setFrom(v as string | null)} /></Field>
          <Field label={t("Cost per straw")} optional><TextInput inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} /></Field>
          <Field label={t("Owner")} optional hint={t("Leave empty when the ranch owns it.")}><RefPicker target="contact" value={owner} onChange={(v) => setOwner(v as string | null)} /></Field>
          <Field label={t("Use rights")} optional><Select value={rights} onChange={(e) => setRights(e.target.value)}><option value="">{t("Choose…")}</option>{Object.entries(RIGHTS_LABELS).map(([k, l]) => <option key={k} value={k}>{t(l)}</option>)}</Select></Field>
        </Disclosure>
        <div className="form-actions"><Button variant="primary" size="lg" type="submit" busy={busy} disabled={!sire || !num(qty)}>{t("Add semen")}</Button></div>
      </form>
    </Sheet>
  );
}

function CollectSheet({ open, onClose, bullId }: { open: boolean; onClose: () => void; bullId?: string }) {
  const { today } = useToday();
  const [bull, setBull] = useState<string | null>(bullId ?? null);
  const [on, setOn] = useState(today);
  const [straws, setStraws] = useState("");
  const [code, setCode] = useState("");
  const [loc, setLoc] = useState<{ tank?: string; canister?: string; cane?: string }>({});
  const [motility, setMotility] = useState("");
  const [by, setBy] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    await send("/m/genetics/inventory/collection", { bull_id: bull, on, straws: num(straws), code: code || undefined, ...loc, motility: num(motility), collector_id: by ?? undefined }, t("Collection recorded"), onClose);
    setBusy(false);
  };
  return (
    <Sheet open={open} onClose={onClose} title={t("Collect from a bull")}>
      <form onSubmit={(e) => { e.preventDefault(); void submit(); }}>
        <Field label={t("Bull")}><RefPicker target="animal" value={bull} onChange={(v) => setBull(v as string | null)} filter={{ sex: "male" }} /></Field>
        <Field label={t("Collected on")}><TextInput type="date" value={on} onChange={(e) => setOn(e.target.value)} /></Field>
        <Field label={t("Straws frozen")}><TextInput inputMode="numeric" value={straws} onChange={(e) => setStraws(e.target.value)} /></Field>
        <Field label={t("Where it's stored")}><Where v={loc} set={setLoc} /></Field>
        <Disclosure label={t("More details")}>
          <Field label={t("Collection code")} optional><TextInput className="mono" value={code} onChange={(e) => setCode(e.target.value)} /></Field>
          <Field label={t("Motility")} optional><div className="input-suffix"><TextInput inputMode="decimal" value={motility} onChange={(e) => setMotility(e.target.value)} /><span className="suffix">%</span></div></Field>
          <Field label={t("Collected by")} optional><RefPicker target="contact" value={by} onChange={(v) => setBy(v as string | null)} /></Field>
        </Disclosure>
        <div className="form-actions"><Button variant="primary" size="lg" type="submit" busy={busy} disabled={!bull || !num(straws)}>{t("Save")}</Button></div>
      </form>
    </Sheet>
  );
}

interface FlushLot { count: string; grade: string; stage: string; freeze: string; tank?: string; canister?: string; cane?: string; numbers: string }

function FlushSheet({ open, onClose, donorId }: { open: boolean; onClose: () => void; donorId?: string }) {
  const { today } = useToday();
  const inv = useApi<InventoryDTO>(open ? "/m/genetics/inventory" : null);
  const [donor, setDonor] = useState<string | null>(donorId ?? null);
  const [sire, setSire] = useState<string | null>(null);
  const [on, setOn] = useState(today);
  const [counts, setCounts] = useState<Record<string, string>>({});
  const [lots, setLots] = useState<FlushLot[]>([{ count: "", grade: "1", stage: "6", freeze: "direct_transfer", numbers: "" }]);
  const [semenLot, setSemenLot] = useState("");
  const [straws, setStraws] = useState("1");
  const [vet, setVet] = useState<string | null>(null);
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);
  const c = (k: string) => num(counts[k] ?? "");
  const semenLots = (inv.data?.items ?? []).filter((i) => i.kind === "semen" && (!sire || i.sire?.id === sire)).flatMap((i) => i.lots.filter((l) => l.on_hand > 0).map((l) => ({ id: l.id, label: `${i.name}${l.code ? ` · ${l.code}` : ""} (${l.on_hand})` })));
  const inTank = lots.reduce((n, l) => n + (num(l.count) ?? 0), 0);
  const submit = async () => {
    setBusy(true);
    await send("/m/genetics/inventory/flush", {
      donor_id: donor, sire_id: sire, on, recovered: c("recovered"), usable: c("usable"), frozen: c("frozen"), fresh: c("fresh"), degenerate: c("degenerate"), unfertilized: c("unfertilized"),
      lots: lots.filter((l) => num(l.count)).map((l) => ({ count: num(l.count), grade: l.grade || undefined, stage: l.stage || undefined, freeze: l.freeze || undefined, tank: l.tank, canister: l.canister, cane: l.cane, numbers: l.numbers || undefined })),
      semen_lot_id: semenLot || undefined, semen_straws: semenLot ? num(straws) : undefined, provider_id: vet ?? undefined, provider_ref: ref || undefined,
    }, t("Flush recorded"), onClose);
    setBusy(false);
  };
  const count = (k: string, label: string) => <Field label={label} optional><TextInput inputMode="numeric" value={counts[k] ?? ""} onChange={(e) => setCounts({ ...counts, [k]: e.target.value })} /></Field>;
  return (
    <Sheet open={open} onClose={onClose} title={t("Record a flush")} size="lg">
      <form onSubmit={(e) => { e.preventDefault(); void submit(); }}>
        <Field label={t("Donor")}><RefPicker target="animal" value={donor} onChange={(v) => setDonor(v as string | null)} filter={{ sex: "female" }} /></Field>
        <Field label={t("Sire")} optional><RefPicker target="animal" value={sire} onChange={(v) => setSire(v as string | null)} filter={{ sex: "male", status: "all" }} /></Field>
        <Field label={t("Flushed on")}><TextInput type="date" value={on} onChange={(e) => setOn(e.target.value)} /></Field>
        <div className="genetics-target-row" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))" }}>
          {count("recovered", t("Recovered"))}{count("usable", t("Usable"))}{count("frozen", t("Frozen"))}{count("fresh", t("Transferred fresh"))}
        </div>
        <Field label={t("Into the tank")} hint={c("frozen") !== undefined && inTank !== c("frozen") ? t("{{count}} frozen, {{n}} placed in the tank so far", { count: c("frozen"), n: inTank }) : undefined}>
          <div className="stack" style={{ gap: 10 }}>
            {lots.map((l, i) => (
              <Card key={i}>
                <div className="stack" style={{ gap: 8 }}>
                  <div className="genetics-target-row" style={{ gridTemplateColumns: "1fr 1fr 44px" }}>
                    <TextInput aria-label={t("Embryos")} placeholder={t("Embryos")} inputMode="numeric" value={l.count} onChange={(e) => setLots(lots.map((x, j) => (j === i ? { ...x, count: e.target.value } : x)))} />
                    <TextInput aria-label={t("Embryo numbers")} placeholder="01-06" value={l.numbers} onChange={(e) => setLots(lots.map((x, j) => (j === i ? { ...x, numbers: e.target.value } : x)))} />
                    <IconButton icon="delete" label={t("Remove")} onClick={() => setLots(lots.filter((_, j) => j !== i))} />
                  </div>
                  <div className="genetics-target-row" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
                    <Select aria-label={t("Grade")} value={l.grade} onChange={(e) => setLots(lots.map((x, j) => (j === i ? { ...x, grade: e.target.value } : x)))}>{Object.entries(EMBRYO_GRADES).map(([k, v]) => <option key={k} value={k}>{t(v)}</option>)}</Select>
                    <Select aria-label={t("Stage")} value={l.stage} onChange={(e) => setLots(lots.map((x, j) => (j === i ? { ...x, stage: e.target.value } : x)))}>{Object.entries(EMBRYO_STAGES).map(([k, v]) => <option key={k} value={k}>{t(v)}</option>)}</Select>
                    <Select aria-label={t("Freezing")} value={l.freeze} onChange={(e) => setLots(lots.map((x, j) => (j === i ? { ...x, freeze: e.target.value } : x)))}>{Object.entries(FREEZE_LABELS).filter(([k]) => k !== "fresh").map(([k, v]) => <option key={k} value={k}>{t(v)}</option>)}</Select>
                  </div>
                  <Where v={l} set={(v) => setLots(lots.map((x, j) => (j === i ? { ...x, ...v } : x)))} />
                </div>
              </Card>
            ))}
            <div><Button size="sm" icon="plus" onClick={() => setLots([...lots, { count: "", grade: "1", stage: "6", freeze: "direct_transfer", numbers: "" }])}>{t("Add a lot")}</Button></div>
          </div>
        </Field>
        <Disclosure label={t("More details")}>
          <div className="genetics-target-row" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))" }}>{count("degenerate", t("Degenerate"))}{count("unfertilized", t("Unfertilized"))}</div>
          <Field label={t("Semen used")} optional><Select value={semenLot} onChange={(e) => setSemenLot(e.target.value)}><option value="">{t("Choose…")}</option>{semenLots.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}</Select></Field>
          {semenLot && <Field label={t("Straws used")}><TextInput inputMode="numeric" value={straws} onChange={(e) => setStraws(e.target.value)} /></Field>}
          <Field label={t("Vet or lab")} optional><RefPicker target="contact" value={vet} onChange={(v) => setVet(v as string | null)} /></Field>
          <Field label={t("Their reference")} optional><TextInput value={ref} onChange={(e) => setRef(e.target.value)} /></Field>
        </Disclosure>
        <div className="form-actions"><Button variant="primary" size="lg" type="submit" busy={busy} disabled={!donor}>{t("Save")}</Button></div>
      </form>
    </Sheet>
  );
}

type MoveAction = keyof typeof INVENTORY_ACTIONS;

function MoveSheet({ lot, item, action: initial, onClose }: { lot: LotDTO; item: GeneticItemDTO; action: MoveAction; onClose: () => void }) {
  const { today, currency } = useToday();
  const [action, setAction] = useState<MoveAction>(initial);
  const [on, setOn] = useState(today);
  const [qty, setQty] = useState("");
  const [counted, setCounted] = useState("");
  const [loc, setLoc] = useState<{ tank?: string; canister?: string; cane?: string }>({});
  const [party, setParty] = useState<string | null>(null);
  const [price, setPrice] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const diff = action === "counted" && num(counted) !== undefined ? num(counted)! - lot.on_hand : 0;
  const submit = async () => {
    setBusy(true);
    await send("/m/genetics/inventory/move", {
      lot_id: lot.id, action, on, quantity: num(qty), counted: num(counted), party_id: party ?? undefined, price: money(price, currency), note: note || undefined,
      to_tank: loc.tank || undefined, to_canister: loc.canister || undefined, to_cane: loc.cane || undefined,
    }, t("Saved"), onClose);
    setBusy(false);
  };
  const ok = action === "counted" ? num(counted) !== undefined : action === "moved" ? !!(loc.tank || loc.canister || loc.cane) : !!num(qty);
  return (
    <Sheet open onClose={onClose} title={`${item.name}${lot.code ? ` · ${lot.code}` : ""}`}>
      <form onSubmit={(e) => { e.preventDefault(); void submit(); }}>
        <p className="muted">{t("{{count}} on hand", { count: lot.on_hand })}{where(lot) ? ` · ${where(lot)}` : ""}</p>
        <Field label={t("What happened")}>
          <div className="choice-chips">{(Object.keys(INVENTORY_ACTIONS) as MoveAction[]).map((a) => <button key={a} type="button" className={cx("choice", action === a && "choice-on")} onClick={() => setAction(a)}>{t(INVENTORY_ACTIONS[a])}</button>)}</div>
        </Field>
        <Field label={t("When")}><TextInput type="date" value={on} onChange={(e) => setOn(e.target.value)} /></Field>
        {action === "counted" ? (
          <Field label={t("Counted on hand")} hint={diff ? t("The book says {{book}}. This adjusts it by {{diff}}.", { book: lot.on_hand, diff: diff > 0 ? `+${diff}` : diff }) : num(counted) !== undefined ? t("Matches the book.") : undefined}>
            <TextInput inputMode="numeric" value={counted} onChange={(e) => setCounted(e.target.value)} />
          </Field>
        ) : (
          <Field label={t("How many")} optional={action === "moved"} hint={action === "moved" ? t("Leave empty to move the whole lot.") : num(qty) && num(qty)! > lot.on_hand ? t("That's more than the book shows. It will be saved and flagged.") : undefined}>
            <TextInput inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value)} />
          </Field>
        )}
        {action === "moved" && <Field label={t("Moved to")}><Where v={loc} set={setLoc} /></Field>}
        {(action === "sold" || action === "shipped" || action === "returned") && <Field label={t("Buyer or receiver")} optional><RefPicker target="contact" value={party} onChange={(v) => setParty(v as string | null)} /></Field>}
        {action === "sold" && <Field label={t("Price")} optional><TextInput inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} /></Field>}
        <Field label={t("Notes")} optional><TextInput value={note} onChange={(e) => setNote(e.target.value)} /></Field>
        <div className="form-actions"><Button variant="primary" size="lg" type="submit" busy={busy} disabled={!ok}>{t("Save")}</Button></div>
      </form>
    </Sheet>
  );
}

function LotLine({ lot, item, onAct }: { lot: LotDTO; item: GeneticItemDTO; onAct?: (a: MoveAction) => void }) {
  const a = lot.attrs;
  const meta = [where(lot), a.collection_date ? t("collected {{date}}", { date: formatDate(String(a.collection_date)) }) : a.flush_date ? t("flushed {{date}}", { date: formatDate(String(a.flush_date)) }) : null,
    a.grade ? t(EMBRYO_GRADES[a.grade as keyof typeof EMBRYO_GRADES] ?? String(a.grade)) : null, a.sexed && a.sexed !== "conventional" ? t(SEXED_LABELS[String(a.sexed)] ?? String(a.sexed)) : null,
    a.rights ? t(RIGHTS_LABELS[String(a.rights)] ?? String(a.rights)) : null, a.cost ? t("{{price}} each", { price: formatMoney(a.cost as Money) }) : null].filter(Boolean).join(" · ");
  return (
    <Row icon={item.kind === "embryo" ? "embryo" : "straw"} tone={lot.on_hand < 0 ? "warn" : "genetics"} title={<><span className="mono">{lot.code ?? t("No lot code")}</span> · <strong>{lot.on_hand}</strong>{lot.reserved ? <span className="muted"> ({plural(lot.reserved, "{{count}} held", "{{count}} held")})</span> : null}</>}
      meta={meta} trailing={onAct ? (
        <>
          {lot.on_hand < 0 && <Chip tone="warn" icon="warn">{t("Count it")}</Chip>}
          <Menu label={t("Do something with this lot")} items={(Object.keys(INVENTORY_ACTIONS) as MoveAction[]).map((x) => ({ label: t(INVENTORY_ACTIONS[x]), onSelect: () => onAct(x) }))} />
        </>
      ) : undefined} />
  );
}

function ItemCard({ item }: { item: GeneticItemDTO }) {
  const short = item.available < 0;
  return (
    <Card to={`/ranch/genetic-inventory/${item.id}`}>
      <div className="genetics-row-head">
        <span className="genetics-row-title">{item.name}</span>
        <span className="genetics-qty">{item.on_hand}</span>
      </div>
      <div className="genetics-row-meta">
        <span>{item.kind === "embryo" ? plural(item.on_hand, "{{count}} embryo", "{{count}} embryos") : plural(item.on_hand, "{{count}} straw", "{{count}} straws")}</span>
        {item.reserved > 0 && <span>{plural(item.reserved, "{{count}} held for plans", "{{count}} held for plans")}</span>}
        {item.reserved > 0 && <span>{plural(Math.max(0, item.available), "{{count}} free", "{{count}} free")}</span>}
        {[...new Set(item.lots.filter((l) => l.on_hand > 0).map((l) => l.tank).filter(Boolean))].map((tank) => <span key={tank}>{tank}</span>)}
      </div>
      {short && <Chip tone="warn" icon="warn">{t("Plans need more than you have")}</Chip>}
    </Card>
  );
}

function Home() {
  useLanguage();
  const can = useCan();
  const q = useApi<InventoryDTO>("/m/genetics/inventory");
  const [view, setView] = useLocalState<string>("genetics.inventoryView", "sire");
  const [params, setParams] = useSearchParams();
  const linked = params.get("do");
  const [sheet, setSheetState] = useState<"receive" | "collect" | "flush" | null>(linked === "receive" || linked === "collect" || linked === "flush" ? linked : null);
  const animalId = params.get("animal") ?? undefined;
  const setSheet = (v: typeof sheet) => { setSheetState(v); if (!v && linked) setParams({}, { replace: true }); };
  const { today } = useToday();
  const semen = (q.data?.items ?? []).filter((i) => i.kind === "semen");
  const embryos = (q.data?.items ?? []).filter((i) => i.kind === "embryo");
  return (
    <Page>
      <PageHeader back={{ to: "/ranch", label: t("Ranch") }} title={t("Semen and embryos")} subtitle={q.data ? [plural(semen.reduce((n, i) => n + i.on_hand, 0), "{{count}} straw", "{{count}} straws"), plural(embryos.reduce((n, i) => n + i.on_hand, 0), "{{count}} embryo", "{{count}} embryos")].join(" · ") : undefined}
        actions={can("record") && <>
          <Button variant="primary" icon="plus" onClick={() => setSheet("receive")}>{t("Receive semen")}</Button>
          <Menu label={t("More")} items={[{ label: t("Collect from a bull"), icon: "straw", onSelect: () => setSheet("collect") }, { label: t("Record a flush"), icon: "embryo", onSelect: () => setSheet("flush") }]} />
        </>} />
      <Tabs value={view} onChange={setView} tabs={[{ id: "sire", label: t("By sire") }, { id: "tank", label: t("By tank") }]} />
      {q.isLoading ? <Loading /> : !q.data ? <ErrorNote error={q.error} retry={() => q.refetch()} /> : !q.data.items.length ? (
        <Empty icon="tank" title={t("Nothing in the tank yet")} body={t("Add the semen you have, or say “bought 40 straws of Inspiration, tank 1, canister 3”.")} action={can("record") && <Button variant="primary" onClick={() => setSheet("receive")}>{t("Receive semen")}</Button>} />
      ) : view === "tank" ? (
        <div className="stack">
          <p className="muted">{t("As of {{date}}", { date: formatDate(today) })}</p>
          {q.data.tanks.map((tank) => (
            <Section key={tank.tank} title={tank.tank || t("No tank recorded")}>
              <div className="genetics-canisters">
                {tank.canisters.map((c) => (
                  <div key={c.canister} className="genetics-canister">
                    <span className="genetics-canister-head">{c.canister ? t("Canister {{c}}", { c: c.canister }) : t("No canister recorded")}</span>
                    {c.lots.map((l) => <Link key={l.lot_id} to={`/ranch/genetic-inventory/${l.item_id}`} className="genetics-straw"><span>{l.name}{l.cane ? <span className="muted"> · {t("cane {{c}}", { c: l.cane })}</span> : null}</span><strong>{l.on_hand}</strong></Link>)}
                  </div>
                ))}
              </div>
            </Section>
          ))}
        </div>
      ) : (
        <div className="stack">
          {semen.length > 0 && <Section title={t("Semen")} count={semen.length}><div className="cards">{semen.map((i) => <ItemCard key={i.id} item={i} />)}</div></Section>}
          {embryos.length > 0 && <Section title={t("Embryos")} count={embryos.length}><div className="cards">{embryos.map((i) => <ItemCard key={i.id} item={i} />)}</div></Section>}
        </div>
      )}
      <ReceiveSheet open={sheet === "receive"} onClose={() => setSheet(null)} sireId={linked === "receive" ? animalId : undefined} />
      <CollectSheet open={sheet === "collect"} onClose={() => setSheet(null)} bullId={linked === "collect" ? animalId : undefined} />
      <FlushSheet open={sheet === "flush"} onClose={() => setSheet(null)} donorId={linked === "flush" ? animalId : undefined} />
    </Page>
  );
}


function ItemPage() {
  useLanguage();
  const { itemId } = useParams();
  const can = useCan();
  const q = useApi<ItemDetailDTO>(`/m/genetics/inventory/${itemId}`);
  const [act, setAct] = useState<{ lot: LotDTO; action: MoveAction } | null>(null);
  if (q.isLoading) return <Loading />;
  if (!q.data) return <ErrorNote error={q.error} retry={() => q.refetch()} />;
  const item = q.data;
  return (
    <Page>
      <PageHeader back={{ to: "/ranch/genetic-inventory", label: t("Semen and embryos") }} eyebrow={item.kind === "embryo" ? t("Embryos") : t("Semen")} title={item.name}
        subtitle={[item.sire ? `${t("Sire")}: ${item.sire.label}` : null, item.donor ? `${t("Donor")}: ${item.donor.label}` : null, item.attrs.registration ? String(item.attrs.registration) : null].filter(Boolean).join(" · ")}
        actions={item.sire && <Button variant="ghost" icon="animal" to={`/cattle/${item.sire.id}`}>{t("Sire's record")}</Button>} />
      <div className="row-actions">
        <Chip tone="genetics">{t("{{count}} on hand", { count: item.on_hand })}</Chip>
        {item.reserved > 0 && <Chip tone="neutral">{plural(item.reserved, "{{count}} held for plans", "{{count}} held for plans")}</Chip>}
        {item.reserved > 0 && <Chip tone={item.available < 0 ? "warn" : "good"}>{plural(item.available, "{{count}} free", "{{count}} free")}</Chip>}
      </div>
      <Section title={t("Lots")} count={item.lots.length}>
        {item.lots.length ? <List>{item.lots.map((l) => <LotLine key={l.id} lot={l} item={item} onAct={can("record") ? (action) => setAct({ lot: l, action }) : undefined} />)}</List> : <p className="muted">{t("No lots.")}</p>}
      </Section>
      {item.reservations.length > 0 && (
        <Section title={t("Held for mating plans")} count={item.reservations.length} hint={t("Held straws are still in the tank until the AI is recorded.")}>
          <List>{item.reservations.map((r, i) => <Row key={i} icon="mating" to={`/work/jobs/${r.job.id}`} title={r.female.label} meta={[r.job.label, plural(r.straws, "{{count}} straw", "{{count}} straws")].join(" · ")} />)}</List>
        </Section>
      )}
      <Section title={t("Ins and outs")}>
        {!item.moves.length ? <p className="muted">{t("Nothing yet.")}</p> : (
          <List>{item.moves.map((m) => <Row key={m.id} icon={m.delta > 0 ? "plus" : "minus"} tone={m.delta > 0 ? "good" : "neutral"} to={m.event ? `/history/${m.event.id}` : undefined}
            title={`${t(MOVE_REASON_LABELS[m.reason] ?? m.reason)} ${m.delta > 0 ? "+" : ""}${m.delta}`} meta={[formatDate(m.occurred_at), m.event ? t(registry.event(m.event.type)?.label ?? m.event.type) : null, ...(m.event?.who ?? []), m.note].filter(Boolean).join(" · ")} />)}</List>
        )}
      </Section>
      {act && <MoveSheet lot={act.lot} item={item} action={act.action} onClose={() => { setAct(null); void q.refetch(); }} />}
    </Page>
  );
}

export function GeneticInventory() {
  return (
    <Routes>
      <Route index element={<Home />} />
      <Route path=":itemId" element={<ItemPage />} />
    </Routes>
  );
}

